import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Button } from '@paulownia/shared/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@paulownia/shared/components/ui/select';
import { Checkbox } from '@paulownia/shared/components/ui/checkbox';
import type { ProductOption, SelectChoice } from '@paulownia/shared/types';
import { X, Plus } from 'lucide-react';

// Helper to generate camelCase ID from label
function toCamelCase(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^./, (chr) => chr.toLowerCase());
}

interface OptionEditorProps {
  option: ProductOption;
  isNew: boolean;
  onChange: (option: ProductOption) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function OptionEditor({
  option,
  isNew,
  onChange,
  onSave,
  onCancel,
}: OptionEditorProps) {
  const updateField = <K extends keyof ProductOption>(field: K, value: ProductOption[K]) => {
    onChange({ ...option, [field]: value });
  };

  const addChoice = () => {
    const newChoice: SelectChoice = { id: `choice_${Date.now()}`, label: '' };
    updateField('choices', [...(option.choices || []), newChoice]);
  };

  const updateChoice = (index: number, updates: Partial<SelectChoice>) => {
    if (!option.choices) return;
    const newChoices = option.choices.map((c, i) =>
      i === index ? { ...c, ...updates, id: updates.label !== undefined ? toCamelCase(updates.label) || c.id : c.id } : c
    );
    updateField('choices', newChoices);
  };

  const removeChoice = (index: number) => {
    if (!option.choices) return;
    updateField('choices', option.choices.filter((_, i) => i !== index));
  };

  const isValid =
    option.label.trim() &&
    !((option.type === 'select' || option.type === 'multiselect') &&
      (!option.choices || option.choices.length === 0 ||
        option.choices.some(c => !c.label.trim())));

  return (
    <div className="p-3 border rounded-lg bg-white shadow-sm space-y-3">
      <div className="flex justify-between items-center">
        <span className="font-medium text-sm">
          {isNew ? 'Nouvelle option' : 'Modifier l\'option'}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Option Label */}
      <div className="space-y-1">
        <Label className="text-xs">Nom de l'option</Label>
        <Input
          value={option.label}
          onChange={(e) => {
            const newLabel = e.target.value;
            onChange({ ...option, label: newLabel, id: toCamelCase(newLabel) || option.id });
          }}
          placeholder="ex: Mouture, Sauce..."
          className="h-8 text-sm"
        />
      </div>

      {/* Option Type */}
      <div className="space-y-1">
        <Label className="text-xs">Type</Label>
        <Select
          value={option.type}
          onValueChange={(v) => {
            const newType = v as ProductOption['type'];
            updateField('type', newType);
            // Clear choices when switching to boolean
            if (newType === 'boolean') {
              updateField('choices', undefined);
              updateField('maxSelections', undefined);
              updateField('required', undefined);
            }
            // Initialize empty choices for select/multiselect
            if ((newType === 'select' || newType === 'multiselect') && !option.choices) {
              updateField('choices', []);
            }
          }}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="boolean">Oui/Non (checkbox)</SelectItem>
            <SelectItem value="select">Choix unique (radio)</SelectItem>
            <SelectItem value="multiselect">Choix multiples (checkboxes)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Choices for select/multiselect */}
      {(option.type === 'select' || option.type === 'multiselect') && (
        <div className="space-y-2">
          <Label className="text-xs">Choix disponibles</Label>
          {option.choices && option.choices.length > 0 ? (
            <div className="space-y-1">
              {option.choices.map((choice, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={choice.label}
                    onChange={(e) => updateChoice(index, { label: e.target.value })}
                    placeholder={`Choix ${index + 1}`}
                    className="h-7 text-sm flex-1"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400">+</span>
                    <Input
                      type="number"
                      step="0.5"
                      value={choice.priceModifier ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateChoice(index, { priceModifier: val === '' ? undefined : parseFloat(val) });
                      }}
                      placeholder="0"
                      className="h-7 text-sm w-16 text-right"
                    />
                    <span className="text-xs text-gray-400">€</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeChoice(index)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Aucun choix defini</p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChoice}
            className="w-full h-7 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Ajouter un choix
          </Button>
        </div>
      )}

      {/* Max selections for multiselect */}
      {option.type === 'multiselect' && (
        <div className="space-y-1">
          <Label className="text-xs">Nombre max de selections (optionnel)</Label>
          <Input
            type="number"
            min="1"
            value={option.maxSelections ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              updateField('maxSelections', val === '' ? undefined : parseInt(val, 10));
            }}
            placeholder="Illimite"
            className="h-8 text-sm w-32"
          />
        </div>
      )}

      {/* Required toggle for select */}
      {option.type === 'select' && (
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="option-required"
            checked={option.required ?? false}
            onCheckedChange={(checked) => updateField('required', checked === true)}
          />
          <Label htmlFor="option-required" className="text-xs cursor-pointer">
            Obligatoire (le client doit choisir)
          </Label>
        </div>
      )}

      {/* Save/Cancel buttons */}
      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={!isValid}
          className="flex-1"
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
